import { useEffect, useState, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Calendar as UICalendar } from "@/components/ui/calendar";
import { useWorkingHours, useStaffMembers, generateTimeSlotsForDay } from "@/hooks/use-working-hours";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday } from "date-fns";
import { pt } from "date-fns/locale";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Check, X, MapPin, Clock, User, Mail, Phone,
  CalendarDays, Home, FileText, Navigation, Pencil, Car, CheckCircle2,
  Plus, Lock, Search, List, LayoutGrid, ChevronLeft, ChevronRight, Columns3, ArrowRight,
  Send, MessageCircle, RefreshCw, Percent, Calendar, Trash2
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import RouteMapDialog from "@/components/admin/RouteMapDialog";
import AddressAutocomplete from "@/components/admin/AddressAutocomplete";

interface Booking {
  id: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  booking_date: string;
  booking_time: string;
  location_type: string;
  address: string;
  total_price: number;
  delivery_fee: number;
  discount_type?: string;
  discount_value?: number;
  discount_amount?: number;
  original_price?: number | null;
  discount_by?: string | null;
  status: string;
  notes: string;
  rejection_reason: string;
  service_id: string | null;
  staff_id: string | null;
  client_record_id?: string | null;
  services: { name: string } | null;
  staff: { name: string } | null;
}

interface ClientRecordLite {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  address: string | null;
  created_at: string;
  signed_at: string | null;
}

const statusLabels: Record<string, string> = {
  pending: "Pendente",
  confirmed: "Confirmada",
  on_the_way: "A Caminho",
  completed: "Concluída",
  cancelled: "Recusada",
  blocked: "Compromisso Pessoal",
};

const statusColors: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  on_the_way: "bg-blue-100 text-blue-800",
  cancelled: "bg-red-100 text-red-800",
  completed: "bg-muted/60 text-muted-foreground",
  blocked: "bg-muted text-muted-foreground",
};

const cardStatusStyles: Record<string, string> = {
  pending: "border-amber-300 bg-amber-50/50",
  confirmed: "border-emerald-300 bg-emerald-50/50",
  on_the_way: "border-blue-300 bg-blue-50/50",
  cancelled: "border-red-300 bg-red-50/50 opacity-60",
  completed: "border-border bg-muted/30 opacity-50",
  blocked: "border-border bg-muted/20 opacity-40",
};

// Time slots are now dynamic from working hours
interface ClientResult {
  user_id: string;
  full_name: string;
  phone: string | null;
  email?: string;
}

const Agenda = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [allBookings, setAllBookings] = useState<Booking[]>([]);
  const [pendingBookings, setPendingBookings] = useState<Booking[]>([]);
  const [viewMode, setViewMode] = useState<"list" | "calendar" | "board">("calendar");
  const [calendarPeriod, setCalendarPeriod] = useState<"day" | "week" | "15days" | "month">("15days");
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [dayDetailDate, setDayDetailDate] = useState<string | null>(null);
  const [filter, setFilter] = useState("pending");
  const [staffFilter, setStaffFilter] = useState("all");
  const [selected, setSelected] = useState<Booking | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [recordPrompt, setRecordPrompt] = useState<{ booking: Booking; hasRecord: boolean } | null>(null);
  const [sendingRecord, setSendingRecord] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>(null);
  const [services, setServices] = useState<{ id: string; name: string; price: number; duration_minutes: number }[]>([]);
  // Ficha de cliente vinculada à reserva
  const [linkedRecord, setLinkedRecord] = useState<ClientRecordLite | null>(null);
  const [recordSearch, setRecordSearch] = useState("");
  const [recordResults, setRecordResults] = useState<ClientRecordLite[]>([]);
  const [searchingRecords, setSearchingRecords] = useState(false);
  const [linkingRecord, setLinkingRecord] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState<Set<string>>(new Set());
  const [bulkStaffId, setBulkStaffId] = useState("");
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { schedule } = useWorkingHours();
  const { staff: allStaffMembers, getTimeSlotsForDate: getStaffTimeSlotsForDate } = useStaffMembers();

  // Generate all possible time slots from all enabled days (for manual booking select)
  const allTimeSlots = useMemo(() => {
    const allSlots = new Set<string>();
    Object.values(schedule).forEach(day => {
      if (day.enabled) {
        generateTimeSlotsForDay(day).forEach(s => allSlots.add(s));
      }
    });
    return Array.from(allSlots).sort();
  }, [schedule]);

  // Generate time slots for a specific date and staff (for block dialog)
  const getTimeSlotsForDateStr = (dateStr: string, staffId?: string): string[] => {
    if (!dateStr) return [];
    const date = new Date(dateStr + "T12:00:00");
    if (staffId) {
      return getStaffTimeSlotsForDate(date, staffId);
    }
    const daySchedule = schedule[date.getDay().toString()];
    if (!daySchedule?.enabled) return [];
    return generateTimeSlotsForDay(daySchedule);
  };

  // Manual booking states
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    client_name: "", client_email: "", client_phone: "",
    booking_date: "", booking_time: "", service_id: "",
    location_type: "studio", address: "", notes: "",
    total_price: 0, delivery_fee: 0, staff_id: "",
  });
  const [staffList, setStaffList] = useState<{ id: string; name: string }[]>([]);
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState<ClientResult[]>([]);
  const [searchingClients, setSearchingClients] = useState(false);

  // Block slot states
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockDates, setBlockDates] = useState<Date[]>([]);
  const [blockTimes, setBlockTimes] = useState<string[]>([]);
  const [blockStaffId, setBlockStaffId] = useState("");
  const [bookedSlotsForBlock, setBookedSlotsForBlock] = useState<string[]>([]);

  const fetchPending = async () => {
    let query = supabase
      .from("bookings")
      .select("*, services:service_id(name), staff:staff_id(name)")
      .eq("status", "pending")
      .order("booking_date", { ascending: true })
      .order("booking_time", { ascending: true });
    if (staffFilter && staffFilter !== "all") {
      query = query.eq("staff_id", staffFilter);
    }
    const { data } = await query;
    setPendingBookings((data as any[]) || []);
  };

  const fetchBookings = async () => {
    let query = supabase
      .from("bookings")
      .select("*, services:service_id(name), staff:staff_id(name)")
      .eq("status", filter)
      .order("booking_date", { ascending: true });
    if (staffFilter && staffFilter !== "all") {
      query = query.eq("staff_id", staffFilter);
    }
    const { data } = await query;
    setBookings((data as any[]) || []);
    fetchPending();
  };


  const fetchServices = async () => {
    const { data } = await supabase.from("services").select("id, name, price, duration_minutes").eq("is_active", true);
    setServices((data as any[]) || []);
  };

  const fetchStaff = async () => {
    const { data } = await supabase.from("staff").select("id, name").eq("is_active", true).order("name");
    setStaffList((data as any[]) || []);
  };

  useEffect(() => { fetchBookings(); }, [filter, staffFilter]);
  useEffect(() => { fetchServices(); fetchStaff(); }, []);

  // Fetch all bookings for calendar view within the visible range
  const fetchAllBookings = async () => {
    const range = getCalendarRange();
    let query = supabase
      .from("bookings")
      .select("*, services:service_id(name), staff:staff_id(name)")
      .neq("status", "cancelled")
      .gte("booking_date", format(range.start, "yyyy-MM-dd"))
      .lte("booking_date", format(range.end, "yyyy-MM-dd"))
      .order("booking_time", { ascending: true });
    if (staffFilter && staffFilter !== "all") {
      query = query.eq("staff_id", staffFilter);
    }
    const { data } = await query;
    setAllBookings((data as any[]) || []);
  };

  const getCalendarRange = () => {
    if (calendarPeriod === "day") return { start: calendarDate, end: calendarDate };
    if (calendarPeriod === "week") return { start: startOfWeek(calendarDate, { weekStartsOn: 1 }), end: endOfWeek(calendarDate, { weekStartsOn: 1 }) };
    if (calendarPeriod === "15days") return { start: calendarDate, end: addDays(calendarDate, 14) };
    // Month grid: full weeks starting Monday so weekdays align correctly
    return {
      start: startOfWeek(startOfMonth(calendarDate), { weekStartsOn: 1 }),
      end: endOfWeek(endOfMonth(calendarDate), { weekStartsOn: 1 }),
    };
  };

  const calendarDays = useMemo(() => {
    const range = getCalendarRange();
    return eachDayOfInterval({ start: range.start, end: range.end });
  }, [calendarDate, calendarPeriod]);

  const getBookingsForDay = (date: Date) => allBookings.filter(b => isSameDay(new Date(b.booking_date), date));

  const navigateCalendar = (dir: number) => {
    if (calendarPeriod === "day") setCalendarDate(addDays(calendarDate, dir));
    else if (calendarPeriod === "week") setCalendarDate(addDays(calendarDate, dir * 7));
    else if (calendarPeriod === "15days") setCalendarDate(addDays(calendarDate, dir * 15));
    else setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + dir, 1));
  };

  useEffect(() => { if (viewMode === "calendar" || viewMode === "board") fetchAllBookings(); }, [viewMode, staffFilter, calendarDate, calendarPeriod]);

  // Fetch booked slots when block date/staff changes
  useEffect(() => {
    if (blockDates.length === 0) { setBookedSlotsForBlock([]); return; }
    // Fetch booked slots for the FIRST selected date (as a proxy for availability)
    supabase.rpc("get_booked_slots", {
      p_date: format(blockDates[0], "yyyy-MM-dd"),
      p_staff_id: blockStaffId || null,
    }).then(({ data }) => {
      setBookedSlotsForBlock((data as string[]) || []);
    });
  }, [blockDates, blockStaffId]);

  // Client search
  const searchClients = async (query: string) => {
    setClientSearch(query);
    if (query.length < 2) { setClientResults([]); return; }
    setSearchingClients(true);
    const { data } = await supabase
      .from("profiles")
      .select("user_id, full_name, phone")
      .or(`full_name.ilike.%${query}%,phone.ilike.%${query}%`)
      .limit(5);
    setClientResults((data as ClientResult[]) || []);
    setSearchingClients(false);
  };

  const selectClient = (client: ClientResult) => {
    setManualForm(prev => ({
      ...prev,
      client_name: client.full_name,
      client_phone: client.phone || "",
      client_email: client.email || "",
    }));
    setClientResults([]);
    setClientSearch("");
  };

  const notify = async (booking: Booking, status: string, extra?: Record<string, any>) => {
    try {
      await supabase.functions.invoke("notify-booking-status", {
        body: {
          booking_id: booking.id,
          status,
          client_email: booking.client_email,
          client_name: booking.client_name,
          client_phone: booking.client_phone,
          service_name: (booking.services as any)?.name || "",
          booking_date: booking.booking_date,
          booking_time: booking.booking_time,
          staff_name: (booking.staff as any)?.name || "",
          staff_id: booking.staff_id,
          booking_address: booking.address,
          total_price: booking.total_price,
          location_type: booking.location_type,
          ...extra,
        },
      });
    } catch (e) {
      console.error("Notification error:", e);
    }
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setProcessing(true);
    await supabase.from("bookings").update({ status: "confirmed" }).eq("id", selected.id);
    await notify(selected, "confirmed");

    // Verificar se o cliente já tem ficha de avaliação preenchida
    let already = false;
    try {
      const { data } = await supabase
        .from("client_records")
        .select("id, signed_at")
        .ilike("email", (selected.client_email || "").trim())
        .limit(1);
      already = !!(data && data.length);
    } catch (e) {
      console.error("client_records check error:", e);
    }

    const booking = selected;
    toast({ title: "Reserva confirmada!" });
    setSelected(null);
    setProcessing(false);
    fetchBookings();

    // Notificação de duplicado se já houver ficha
    if (already) {
      toast({
        title: "Cliente já possui uma ficha registada",
        description: "Deseja atualizar a ficha anterior ou enviar o link para nova submissão?",
      });
    }

    setRecordPrompt({ booking, hasRecord: already });
  };

  const sendClientRecord = async (channels: string[]) => {
    if (!recordPrompt) return;
    setSendingRecord(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-client-record", {
        body: {
          client_name: recordPrompt.booking.client_name,
          client_email: recordPrompt.booking.client_email,
          client_phone: recordPrompt.booking.client_phone,
          channels,
          booking_id: recordPrompt.booking.id,
          locale: (recordPrompt.booking as any).locale || "pt-PT",
          site_url: window.location.origin,
        },
      });
      if (error) throw error;
      const res = (data as any)?.results || {};
      const ok = Object.values(res).some(Boolean);
      toast({
        title: ok ? "Mensagem enviada ao cliente" : "Não foi possível enviar",
        description: ok
          ? Object.entries(res).map(([k, v]) => `${k}: ${v ? "ok" : "falhou"}`).join(" · ")
          : "Verifique as integrações de WhatsApp/Email em Definições > APIs.",
        variant: ok ? undefined : "destructive",
      });
    } catch (e: any) {
      toast({ title: "Erro no envio", description: e?.message || String(e), variant: "destructive" });
    }
    setSendingRecord(false);
    setRecordPrompt(null);
  };


  const handleOnTheWay = async () => {
    if (!selected) return;
    setProcessing(true);
    await supabase.from("bookings").update({ status: "on_the_way" }).eq("id", selected.id);
    await notify(selected, "on_the_way");
    toast({ title: "Cliente notificado, A caminho!" });
    setSelected(null);
    setProcessing(false);
    fetchBookings();
  };

  const handleComplete = async () => {
    if (!selected) return;
    setProcessing(true);
    await supabase.from("bookings").update({ status: "completed" }).eq("id", selected.id);
    await notify(selected, "completed");
    toast({ title: "Sessão concluída!" });
    setSelected(null);
    setProcessing(false);
    fetchBookings();
  };

  const handleReject = async () => {
    if (!selected || !rejectionReason.trim()) {
      toast({ title: "Erro", description: "Indique o motivo da recusa.", variant: "destructive" });
      return;
    }
    setProcessing(true);
    await supabase.from("bookings").update({
      status: "cancelled",
      rejection_reason: rejectionReason,
    }).eq("id", selected.id);
    await notify(selected, "cancelled", { rejection_reason: rejectionReason });
    toast({ title: "Reserva recusada." });
    setSelected(null);
    setRejecting(false);
    setRejectionReason("");
    setProcessing(false);
    fetchBookings();
  };

  const handleUnblock = async () => {
    if (!selected) return;
    setProcessing(true);
    await supabase.from("bookings").delete().eq("id", selected.id);
    toast({ title: "Horário desbloqueado!" });
    setSelected(null);
    setProcessing(false);
    fetchBookings();
    fetchAllBookings();
  };

  const handleBulkDelete = async () => {
    if (selectedBookings.size === 0) return;
    const ids = Array.from(selectedBookings);
    setProcessing(true);
    const { error } = await supabase.from("bookings").delete().in("id", ids);
    if (error) {
      toast({ title: "Erro ao eliminar", description: error.message, variant: "destructive" });
    } else {
      toast({ title: `${ids.length} itens eliminados!` });
      setSelectedBookings(new Set());
      fetchBookings();
      fetchAllBookings();
    }
    setProcessing(false);
  };

  const handleBulkUpdateStaff = async () => {
    if (selectedBookings.size === 0 || !bulkStaffId) return;
    const ids = Array.from(selectedBookings);
    setProcessing(true);
    const { error } = await supabase.from("bookings").update({ staff_id: bulkStaffId }).in("id", ids);
    if (error) {
      toast({ title: "Erro ao atualizar", description: error.message, variant: "destructive" });
    } else {
      toast({ title: `${ids.length} itens atualizados!` });
      setSelectedBookings(new Set());
      setBulkEditOpen(false);
      setBulkStaffId("");
      fetchBookings();
      fetchAllBookings();
    }
    setProcessing(false);
  };

  const toggleBookingSelection = (id: string) => {
    const next = new Set(selectedBookings);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedBookings(next);
  };

  const startEditing = () => {
    if (!selected) return;
    const basePrice = Number(selected.original_price ?? (Number(selected.total_price) + Number(selected.discount_amount || 0)));
    setEditForm({
      client_name: selected.client_name,
      client_email: selected.client_email,
      client_phone: selected.client_phone,
      booking_date: selected.booking_date,
      booking_time: selected.booking_time.slice(0, 5),
      location_type: selected.location_type,
      address: selected.address || "",
      total_price: basePrice,
      delivery_fee: selected.delivery_fee,
      discount_type: selected.discount_type && selected.discount_type !== "none" ? selected.discount_type : "none",
      discount_value: Number(selected.discount_value || 0),
      notes: selected.notes || "",
      service_id: selected.service_id || "",
    });
    setEditing(true);
  };

  // Computed discount for the edit form
  const editBase = Number(editForm?.total_price) || 0;
  const editDiscountAmount = (() => {
    if (!editForm || editForm.discount_type === "none") return 0;
    const v = Number(editForm.discount_value) || 0;
    const amount = editForm.discount_type === "percent" ? (editBase * v) / 100 : v;
    return Math.min(Math.max(amount, 0), editBase);
  })();
  const editFinalPrice = Math.max(0, editBase - editDiscountAmount);

  const handleSaveEdit = async () => {
    if (!selected || !editForm) return;
    setProcessing(true);

    try {
      const hasDiscount = editDiscountAmount > 0;
      const discountAuthor = user?.email || user?.id || "admin";

      // Update the booking
      const { error: bookingError } = await supabase.from("bookings").update({
        client_name: editForm.client_name,
        client_email: editForm.client_email,
        client_phone: editForm.client_phone,
        booking_date: editForm.booking_date,
        booking_time: editForm.booking_time,
        location_type: editForm.location_type,
        address: editForm.address || null,
        total_price: Number(editFinalPrice.toFixed(2)),
        original_price: Number(editBase.toFixed(2)),
        discount_type: hasDiscount ? editForm.discount_type : "none",
        discount_value: hasDiscount ? Number(editForm.discount_value) || 0 : 0,
        discount_amount: Number(editDiscountAmount.toFixed(2)),
        discount_by: hasDiscount ? (selected.discount_by && Number(selected.discount_amount || 0) === Number(editDiscountAmount.toFixed(2)) ? selected.discount_by : discountAuthor) : null,
        discount_at: hasDiscount ? new Date().toISOString() : null,
        delivery_fee: Number(editForm.delivery_fee),
        notes: editForm.notes || null,
        service_id: editForm.service_id || null,
      }).eq("id", selected.id);

      if (bookingError) throw bookingError;

      // Also try to update the matching client record if it exists
      if (editForm.client_email || editForm.client_phone) {
        const { data: existingRecord } = await supabase
          .from("client_records")
          .select("id")
          .or(`email.ilike.${editForm.client_email},phone.eq.${editForm.client_phone}`)
          .maybeSingle();

        if (existingRecord) {
          await supabase.from("client_records").update({
            full_name: editForm.client_name,
            email: editForm.client_email,
            phone: editForm.client_phone,
            address: editForm.address || null,
          }).eq("id", existingRecord.id);
        }
      }

      toast({ title: "Reserva atualizada!" });
      setEditing(false);
      setSelected(null);
      fetchBookings();
    } catch (error: any) {
      toast({ title: "Erro ao guardar", description: error.message, variant: "destructive" });
    } finally {
      setProcessing(false);
    }
  };

  const openBooking = (b: Booking) => {
    setSelected(b);
    setRejecting(false);
    setRejectionReason("");
    setEditing(false);
    setEditForm(null);
    setRecordSearch("");
    setRecordResults([]);
  };

  const RECORD_FIELDS = "id, full_name, email, phone, address, created_at, signed_at";

  // Carrega a ficha vinculada (ou sugere por email/telefone) sempre que uma reserva é aberta
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!selected || selected.status === "blocked") { setLinkedRecord(null); return; }
      try {
        if (selected.client_record_id) {
          const { data } = await supabase
            .from("client_records")
            .select(RECORD_FIELDS)
            .eq("id", selected.client_record_id)
            .maybeSingle();
          if (!cancelled) setLinkedRecord((data as any) || null);
          return;
        }
        const email = (selected.client_email || "").trim();
        if (!email) { setLinkedRecord(null); return; }
        const { data } = await supabase
          .from("client_records")
          .select(RECORD_FIELDS)
          .ilike("email", email)
          .order("created_at", { ascending: false })
          .limit(1);
        if (!cancelled) setLinkedRecord((data && data[0] as any) || null);
      } catch {
        if (!cancelled) setLinkedRecord(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selected]);

  const searchRecords = async () => {
    const term = recordSearch.trim();
    if (term.length < 2) {
      toast({ title: "Escreva pelo menos 2 caracteres", variant: "destructive" });
      return;
    }
    setSearchingRecords(true);
    const { data, error } = await supabase
      .from("client_records")
      .select(RECORD_FIELDS)
      .or(`full_name.ilike.%${term}%,email.ilike.%${term}%,phone.ilike.%${term}%`)
      .order("created_at", { ascending: false })
      .limit(10);
    if (error) toast({ title: "Erro na pesquisa", description: error.message, variant: "destructive" });
    setRecordResults((data as any) || []);
    setSearchingRecords(false);
  };

  /** Vincula a ficha à reserva e atualiza os dados do cliente na reserva */
  const linkRecordToBooking = async (record: ClientRecordLite) => {
    if (!selected) return;
    setLinkingRecord(true);
    try {
      const payload: any = {
        client_record_id: record.id,
        client_name: record.full_name || selected.client_name,
        client_email: record.email || selected.client_email,
        client_phone: record.phone || selected.client_phone,
      };
      if (selected.location_type === "home" && record.address) payload.address = record.address;

      const { error } = await supabase.from("bookings").update(payload).eq("id", selected.id);
      if (error) throw error;

      setSelected({ ...selected, ...payload });
      setLinkedRecord(record);
      setRecordResults([]);
      setRecordSearch("");
      toast({ title: "Ficha vinculada!", description: "Os dados da reserva foram atualizados com a ficha do cliente." });
      fetchBookings();
    } catch (e: any) {
      toast({ title: "Erro ao vincular ficha", description: e?.message || String(e), variant: "destructive" });
    }
    setLinkingRecord(false);
  };

  const unlinkRecord = async () => {
    if (!selected) return;
    setLinkingRecord(true);
    const { error } = await supabase.from("bookings").update({ client_record_id: null }).eq("id", selected.id);
    if (error) {
      toast({ title: "Erro ao desvincular", description: error.message, variant: "destructive" });
    } else {
      setSelected({ ...selected, client_record_id: null });
      setLinkedRecord(null);
      toast({ title: "Ficha desvinculada." });
      fetchBookings();
    }
    setLinkingRecord(false);
  };

  /** Sincroniza novamente os dados da reserva a partir da ficha vinculada */
  const syncFromRecord = async () => {
    if (linkedRecord) await linkRecordToBooking(linkedRecord);
  };


  // Manual booking creation
  const handleCreateManualBooking = async () => {
    if (!manualForm.booking_date || !manualForm.booking_time || !manualForm.client_name) {
      toast({ title: "Erro", description: "Preencha data, hora e nome do cliente.", variant: "destructive" });
      return;
    }
    setProcessing(true);

    const selectedService = services.find(s => s.id === manualForm.service_id);
    const totalPrice = manualForm.total_price || (selectedService?.price || 0) + manualForm.delivery_fee;

    const { error } = await supabase.from("bookings").insert({
      client_name: manualForm.client_name,
      client_email: manualForm.client_email,
      client_phone: manualForm.client_phone,
      booking_date: manualForm.booking_date,
      booking_time: manualForm.booking_time,
      service_id: manualForm.service_id || null,
      location_type: manualForm.location_type,
      address: manualForm.address || null,
      notes: manualForm.notes || null,
      total_price: totalPrice,
      delivery_fee: manualForm.delivery_fee,
      status: "confirmed",
      staff_id: manualForm.staff_id || null,
    });

    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Reserva criada com sucesso!" });
      setManualOpen(false);
      setManualForm({
        client_name: "", client_email: "", client_phone: "",
        booking_date: "", booking_time: "", service_id: "",
        location_type: "studio", address: "", notes: "",
        total_price: 0, delivery_fee: 0, staff_id: "",
      });
      fetchBookings();
    }
    setProcessing(false);
  };

  // Block slots
  const handleBlockSlots = async () => {
    if (blockDates.length === 0 || blockTimes.length === 0 || !blockStaffId) {
      toast({ title: "Erro", description: "Selecione colaborador, data(s) e pelo menos um horário.", variant: "destructive" });
      return;
    }
    setProcessing(true);

    const dates = blockDates.map(d => format(d, "yyyy-MM-dd"));
    const allInserts: any[] = [];

    dates.forEach(d => {
      blockTimes.forEach(time => {
        allInserts.push({
          booking_date: d,
          booking_time: time,
          client_name: "Compromisso Pessoal",
          client_email: "",
          client_phone: "",
          location_type: "studio",
          status: "blocked",
          staff_id: blockStaffId,
          total_price: 0,
          delivery_fee: 0,
        });
      });
    });

    const { error } = await supabase.from("bookings").insert(allInserts);

    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({ title: `${allInserts.length} horário(s) bloqueado(s) em ${dates.length} dia(s)!` });
      setBlockOpen(false);
      setBlockDates([]);
      setBlockTimes([]);
      setBlockStaffId("");
      fetchBookings();
      fetchAllBookings();
    }
    setProcessing(false);
  };

  const toggleBlockTime = (slot: string) => {
    setBlockTimes(prev =>
      prev.includes(slot) ? prev.filter(t => t !== slot) : [...prev, slot]
    );
  };

  const boardColumns = ["pending", "confirmed", "on_the_way", "completed", "cancelled"] as const;

  const handleQuickStatusChange = async (booking: Booking, newStatus: string, opts?: { silent?: boolean; extra?: Record<string, any> }) => {
    setProcessing(true);
    // Optimistic update
    setAllBookings(prev => prev.map(b => b.id === booking.id ? { ...b, status: newStatus } : b));
    setBookings(prev => prev.map(b => b.id === booking.id ? { ...b, status: newStatus } : b));
    const updates: any = { status: newStatus };
    if (opts?.extra?.rejection_reason) updates.rejection_reason = opts.extra.rejection_reason;
    await supabase.from("bookings").update(updates).eq("id", booking.id);
    if (!opts?.silent) {
      await notify(booking, newStatus, opts?.extra);
    }
    toast({ title: `Reserva movida para "${statusLabels[newStatus]}"` });
    if (selected?.id === booking.id) setSelected({ ...booking, status: newStatus, ...(opts?.extra || {}) });
    fetchAllBookings();
    fetchBookings();
    setProcessing(false);
  };

  const handleResendNotification = async (channel: "whatsapp" | "email" | "both") => {
    if (!selected) return;
    setProcessing(true);
    try {
      await supabase.functions.invoke("notify-booking-status", {
        body: {
          booking_id: selected.id,
          status: selected.status,
          client_email: channel === "whatsapp" ? "" : selected.client_email,
          client_name: selected.client_name,
          client_phone: channel === "email" ? "" : selected.client_phone,
          service_name: (selected.services as any)?.name || "",
          booking_date: selected.booking_date,
          booking_time: selected.booking_time,
          staff_name: (selected.staff as any)?.name || "",
          staff_id: selected.staff_id,
          booking_address: selected.address,
          total_price: selected.total_price,
          location_type: selected.location_type,
          rejection_reason: selected.rejection_reason,
        },
      });
      const label = channel === "whatsapp" ? "WhatsApp" : channel === "email" ? "Email" : "WhatsApp e Email";
      toast({ title: ` Notificação reenviada por ${label}` });
    } catch (e: any) {
      toast({ title: "Erro ao reenviar", description: e?.message || String(e), variant: "destructive" });
    }
    setProcessing(false);
  };

  const onDragEnd = useCallback((result: DropResult) => {
    const { draggableId, destination, source } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    const booking = allBookings.find(b => b.id === draggableId);
    if (!booking) return;
    const newStatus = destination.droppableId;
    handleQuickStatusChange(booking, newStatus);
  }, [allBookings]);

  return (
    <>
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="font-display text-3xl font-bold">Agenda</h1>
          <div className="flex gap-2">
            <div className="flex border border-border rounded-full overflow-hidden">
              <button onClick={() => setViewMode("calendar")} className={`px-3 py-1.5 text-xs flex items-center gap-1 transition-colors ${viewMode === "calendar" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                <CalendarDays size={14} /> Calendário
              </button>
              <button onClick={() => setViewMode("list")} className={`px-3 py-1.5 text-xs flex items-center gap-1 transition-colors ${viewMode === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                <List size={14} /> Lista
              </button>
              <button onClick={() => setViewMode("board")} className={`px-3 py-1.5 text-xs flex items-center gap-1 transition-colors ${viewMode === "board" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                <Columns3 size={14} /> Quadro
              </button>
            </div>
            <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => setBlockOpen(true)}>
              <Lock size={14} /> Bloquear Horário
            </Button>
            <Button size="sm" className="rounded-full gap-1.5" onClick={() => setManualOpen(true)}>
              <Plus size={14} /> Nova Reserva
            </Button>
          </div>
        </div>

        {/* Staff filter */}
        <div className="flex items-center gap-3 mb-6">
          <Label className="text-sm text-muted-foreground whitespace-nowrap">Filtrar colaborador:</Label>
          <Select value={staffFilter} onValueChange={setStaffFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {staffList.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Pendentes em destaque */}
        {pendingBookings.length > 0 && (
          <div className="mb-6 rounded-2xl border-2 border-amber-400/60 bg-amber-50 dark:bg-amber-950/20 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-lg font-bold text-amber-900 dark:text-amber-200 flex items-center gap-2">
                <Clock size={16} /> Pendentes de confirmação
                <Badge className="bg-amber-500 text-white hover:bg-amber-500">{pendingBookings.length}</Badge>
              </h2>
              {viewMode !== "list" && (
                <Button size="sm" variant="outline" className="rounded-full h-7 text-xs"
                  onClick={() => { setFilter("pending"); setViewMode("list"); }}>
                  Ver todas na lista
                </Button>
              )}
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {pendingBookings.slice(0, 6).map(b => (
                <button key={b.id} onClick={() => openBooking(b)}
                  className="text-left rounded-xl border border-amber-300/70 bg-card p-3 hover:border-amber-500 hover:shadow-sm transition-all">
                  <p className="text-sm font-semibold text-foreground truncate">{b.client_name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {format(new Date(b.booking_date + "T12:00:00"), "EEE, d 'de' MMM", { locale: pt })} · {b.booking_time.slice(0, 5)}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{(b.services as any)?.name}</p>
                </button>
              ))}
            </div>
            {pendingBookings.length > 6 && (
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-2">+{pendingBookings.length - 6} outras reservas pendentes</p>
            )}
          </div>
        )}



        {viewMode === "list" && (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {["pending", "confirmed", "on_the_way", "completed", "cancelled", "blocked"].map((s) => (
                <Button
                  key={s}
                  variant={filter === s ? "default" : "outline"}
                  size="sm"
                  className="rounded-full"
                  onClick={() => setFilter(s)}
                >
                  {statusLabels[s]}
                </Button>
              ))}
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto text-xs"
                onClick={() => {
                  const allIds = new Set(bookings.map(b => b.id));
                  if (selectedBookings.size === allIds.size) setSelectedBookings(new Set());
                  else setSelectedBookings(allIds);
                }}
              >
                {selectedBookings.size === bookings.length ? "Desmarcar tudo" : "Selecionar estes"}
              </Button>
            </div>

            {selectedBookings.size > 0 && (
              <div className="mb-4 p-3 bg-muted/50 rounded-xl flex items-center justify-between gap-2 animate-in fade-in slide-in-from-top-2 flex-wrap">
                <span className="text-sm font-medium">{selectedBookings.size} selecionados</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setBulkEditOpen(true)} className="rounded-full gap-2">
                    <User size={14} /> Alterar Colaborador
                  </Button>
                  <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={processing} className="rounded-full gap-2">
                    <Trash2 size={14} /> Eliminar
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {bookings.length === 0 && (
                <p className="text-muted-foreground text-center py-12">
                  Nenhuma reserva {statusLabels[filter]?.toLowerCase() || ""} encontrada.
                </p>
              )}
               {bookings.map((b) => (
                <div key={b.id} className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex items-center">
                    <input 
                      type="checkbox" 
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                      checked={selectedBookings.has(b.id)}
                      onChange={() => toggleBookingSelection(b.id)}
                    />
                  </div>
                  <button
                    onClick={() => openBooking(b)}
                    className={`w-full text-left rounded-xl border p-5 pl-12 hover:shadow-sm transition-all ${cardStatusStyles[b.status] || "border-border bg-card"} hover:border-primary/30`}
                  >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-foreground">
                        {b.status === "blocked"
                          ? `🏠 Compromisso Pessoal, ${(b.staff as any)?.name || "Sem colaborador"}`
                          : (b.client_name || b.client_email)}
                      </h3>
                      <p className="text-sm text-muted-foreground">{(b.services as any)?.name}</p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusColors[b.status]}`}>
                      {statusLabels[b.status] || b.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {format(new Date(b.booking_date), "d MMM yyyy", { locale: pt })} às {b.booking_time.slice(0, 5)}
                    </span>
                    {b.status !== "blocked" && (
                      <>
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {b.location_type === "home" ? "Domicílio" : "Espaço"}
                        </span>
                        <span className="font-semibold text-foreground">€{Number(b.total_price).toFixed(2)}</span>
                      </>
                    )}
                    {(b.staff as any)?.name && b.status !== "blocked" && (
                      <span className="flex items-center gap-1">
                        <User size={14} /> {(b.staff as any).name}
                      </span>
                    )}
                  </div>
                </button>
              </div>
            ))}
            </div>
          </>
        )}

        {viewMode === "board" && (
          /* Trello-style Board View */
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
              {boardColumns.map(status => {
                const col = allBookings.filter(b => b.status === status);
                const nextStatusMap: Record<string, string> = { pending: "confirmed", confirmed: "on_the_way", on_the_way: "completed" };
                const nextStatus = nextStatusMap[status];
                return (
                  <div key={status} className="flex-shrink-0 w-72 sm:w-80 rounded-xl border border-border bg-muted/30 flex flex-col max-h-[calc(100vh-260px)]">
                    <div className="p-3 border-b border-border flex items-center justify-between sticky top-0 bg-muted/30 rounded-t-xl">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${statusColors[status]}`}>
                          {statusLabels[status]}
                        </span>
                        <Badge variant="secondary" className="text-[10px] h-5">{col.length}</Badge>
                      </div>
                    </div>
                    <Droppable droppableId={status}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`flex-1 overflow-y-auto p-2 space-y-2 transition-colors ${snapshot.isDraggingOver ? "bg-primary/5" : ""}`}
                        >
                          {col.length === 0 && !snapshot.isDraggingOver && <p className="text-xs text-muted-foreground text-center py-6">Sem reservas</p>}
                          {col.map((b, index) => (
                            <Draggable key={b.id} draggableId={b.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`rounded-lg border p-3 shadow-sm transition-all space-y-2 ${cardStatusStyles[b.status] || "border-border bg-card"} ${snapshot.isDragging ? "shadow-lg ring-2 ring-primary/30 rotate-1 !opacity-100" : "hover:shadow-md"}`}
                                >
                                  <button onClick={() => openBooking(b)} className="w-full text-left space-y-1">
                                    <p className="font-semibold text-sm text-foreground leading-tight">{b.client_name || b.client_email}</p>
                                    <p className="text-xs text-muted-foreground">{(b.services as any)?.name}</p>
                                  </button>
                                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                                    <span className="flex items-center gap-1"><CalendarDays size={10} />{format(new Date(b.booking_date), "d MMM", { locale: pt })}</span>
                                    <span className="flex items-center gap-1"><Clock size={10} /> {b.booking_time.slice(0, 5)}</span>
                                    <span className="flex items-center gap-1"><MapPin size={10} /> {b.location_type === "home" ? "Dom." : "Espaço"}</span>
                                  </div>
                                  <div className="flex items-center justify-between pt-1 border-t border-border">
                                    <div className="flex items-center gap-2">
                                      {(b.staff as any)?.name && (
                                        <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                          <User size={10} /> {(b.staff as any).name.split(" ")[0]}
                                        </span>
                                      )}
                                      <span className="text-xs font-bold text-primary">€{Number(b.total_price).toFixed(2)}</span>
                                    </div>
                                    {nextStatus && (
                                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] gap-1 text-muted-foreground hover:text-primary"
                                        disabled={processing}
                                        onClick={() => handleQuickStatusChange(b, nextStatus)}>
                                        <ArrowRight size={10} /> {statusLabels[nextStatus]}
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        )}

        {viewMode === "calendar" && (
          /* Calendar View */
          <div className="space-y-4">
            {/* Period selector + navigation */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex border border-border rounded-full overflow-hidden">
                {([["day", "Dia"], ["week", "Semana"], ["15days", "15 Dias"], ["month", "Mês"]] as const).map(([key, label]) => (
                  <button key={key} onClick={() => setCalendarPeriod(key)}
                    className={`px-3 py-1.5 text-xs transition-colors ${calendarPeriod === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => navigateCalendar(-1)}><ChevronLeft size={16} /></Button>
                <button onClick={() => setCalendarDate(new Date())} className="text-sm font-semibold text-foreground min-w-[160px] text-center hover:text-primary transition-colors">
                  {calendarPeriod === "day" && format(calendarDate, "d 'de' MMMM yyyy", { locale: pt })}
                  {calendarPeriod === "week" && `${format(startOfWeek(calendarDate, { weekStartsOn: 1 }), "d MMM", { locale: pt })}, ${format(endOfWeek(calendarDate, { weekStartsOn: 1 }), "d MMM yyyy", { locale: pt })}`}
                  {calendarPeriod === "15days" && `${format(calendarDate, "d MMM", { locale: pt })}, ${format(addDays(calendarDate, 14), "d MMM yyyy", { locale: pt })}`}
                  {calendarPeriod === "month" && format(calendarDate, "MMMM yyyy", { locale: pt })}
                </button>
                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => navigateCalendar(1)}><ChevronRight size={16} /></Button>
              </div>
            </div>

            {/* Calendar grid */}
            <div className={`grid gap-2 ${calendarPeriod === "day" ? "grid-cols-1" : "grid-cols-3 sm:grid-cols-7"}`}>
              {(calendarPeriod === "month" || calendarPeriod === "week") && ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"].map(d => (
                <div key={d} className="hidden sm:block text-center text-[11px] sm:text-xs font-semibold text-muted-foreground py-1">{d}</div>
              ))}

              {calendarDays.map(day => {
                const dayBookings = getBookingsForDay(day);
                const today = isToday(day);
                const outsideMonth = calendarPeriod === "month" && day.getMonth() !== calendarDate.getMonth();
                return (
                  <button key={day.toISOString()} onClick={() => setDayDetailDate(format(day, "yyyy-MM-dd"))}
                    className={`rounded-xl border text-left transition-all hover:border-primary/40 hover:shadow-sm min-h-[120px] sm:min-h-[95px] md:min-h-[80px] p-3 sm:p-2.5 md:p-2 ${today ? "border-primary bg-primary/5" : "border-border bg-card"} ${outsideMonth ? "opacity-40" : ""} ${calendarPeriod === "day" ? "min-h-[300px]" : ""}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-sm sm:text-xs font-bold ${today ? "text-primary" : "text-foreground"}`}>
                          {format(day, calendarPeriod === "day" ? "EEEE, d MMMM" : calendarPeriod === "15days" ? "EEE d MMM" : "d", { locale: pt })}
                        </span>
                      </div>
                      {dayBookings.length > 0 && <Badge variant="secondary" className="text-[10px] sm:text-[9px] h-4 sm:h-4 px-1.5 sm:px-1">{dayBookings.length}</Badge>}
                    </div>
                    <div className="space-y-1.5 sm:space-y-1">
                      {dayBookings.slice(0, calendarPeriod === "day" ? 20 : 3).map(b => (
                        <div key={b.id} className={`rounded-md px-2 py-1 sm:px-1.5 sm:py-0.5 text-xs sm:text-[10px] leading-tight truncate ${statusColors[b.status]}`}>
                          <span className="font-semibold">{b.booking_time.slice(0, 5)}</span>
                          {" "}{b.status === "blocked" ? "Opção Pessoal" : (b.client_name?.split(" ")[0] || "")}
                          {(b.staff as any)?.name && <span className="opacity-70"> · {(b.staff as any).name.split(" ")[0]}</span>}
                        </div>
                      ))}
                      {dayBookings.length > (calendarPeriod === "day" ? 20 : 3) && (
                        <p className="text-[10px] sm:text-[9px] text-muted-foreground text-center">+{dayBookings.length - (calendarPeriod === "day" ? 20 : 3)} mais</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Day Detail Dialog */}
        <Dialog open={!!dayDetailDate} onOpenChange={(open) => !open && setDayDetailDate(null)}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2">
                <CalendarDays size={18} />
                {dayDetailDate && format(new Date(dayDetailDate + "T12:00:00"), "EEEE, d 'de' MMMM 'de' yyyy", { locale: pt })}
              </DialogTitle>
            </DialogHeader>
            {dayDetailDate && (() => {
              const dayBk = allBookings.filter(b => b.booking_date === dayDetailDate).sort((a, b) => a.booking_time.localeCompare(b.booking_time));
              return dayBk.length === 0 ? (
                <p className="text-muted-foreground text-center py-8 text-sm">Sem agendamentos neste dia.</p>
              ) : (
                <div className="space-y-2">
                  {dayBk.map(b => (
                    <button key={b.id} onClick={() => { setDayDetailDate(null); setTimeout(() => openBooking(b), 200); }}
                      className="w-full text-left rounded-lg border border-border p-3 hover:border-primary/30 transition-all space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm text-foreground">
                          {b.booking_time.slice(0, 5)}, {b.status === "blocked" ? "🏠 Compromisso Pessoal" : b.client_name}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[b.status]}`}>
                          {statusLabels[b.status]}
                        </span>
                      </div>
                      {b.status !== "blocked" && (
                        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                          <span>{(b.services as any)?.name}</span>
                          {(b.staff as any)?.name && <span className="flex items-center gap-1"><User size={10} /> {(b.staff as any).name}</span>}
                          <span className="font-semibold text-primary">€{Number(b.total_price).toFixed(2)}</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Booking Detail / Edit Dialog */}
        <Dialog open={!!selected} onOpenChange={(open) => { if (!open) { setSelected(null); setEditing(false); } }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            {selected && !editing && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle className="font-display text-2xl">
                      {selected.status === "blocked" ? "Horário Bloqueado" : "Detalhes da Reserva"}
                    </DialogTitle>
                    {selected.status !== "blocked" && (
                      <Button variant="outline" size="sm" className="gap-1" onClick={startEditing}>
                        <Pencil size={14} /> Editar
                      </Button>
                    )}
                  </div>
                </DialogHeader>

                {selected.status === "blocked" ? (
                  <div className="space-y-4 py-2">
                    <div className="rounded-xl border border-border p-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <User size={14} />
                        Colaborador: {(selected.staff as any)?.name || "Não definido"}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CalendarDays size={14} />
                        {format(new Date(selected.booking_date), "d 'de' MMMM 'de' yyyy", { locale: pt })}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock size={14} /> {selected.booking_time.slice(0, 5)}
                      </div>
                    </div>
                    <Button variant="destructive" className="w-full rounded-full gap-2" onClick={handleUnblock} disabled={processing}>
                      <X size={16} /> {processing ? "..." : "Desbloquear Horário"}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4 py-2">
                    <div className="flex justify-between items-center">
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusColors[selected.status]}`}>
                        {statusLabels[selected.status] || selected.status}
                      </span>
                      <span className="text-lg font-bold text-primary">€{Number(selected.total_price).toFixed(2)}</span>
                    </div>

                    <div className="rounded-xl border border-border p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                          <span className="text-lg"></span>
                        </div>
                        <p className="font-semibold text-foreground">{(selected.services as any)?.name || "Serviço"}</p>
                      </div>
                    </div>

                    <div className="rounded-xl border border-border p-4 space-y-2 relative group">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-foreground">Dados do cliente</h4>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 text-[10px] gap-1 px-2"
                          onClick={startEditing}
                        >
                          <Pencil size={10} /> Editar
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground"><User size={14} /> {selected.client_name}</div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground"><Mail size={14} /> {selected.client_email}</div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground"><Phone size={14} /> {selected.client_phone || "Não indicado"}</div>
                    </div>

                    {/* Ficha de cliente vinculada */}
                    <div className="rounded-xl border border-border p-4 space-y-3">
                      <Label className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <FileText size={14} /> Ficha do cliente
                      </Label>

                      {linkedRecord ? (
                        <div className="rounded-lg bg-muted/40 border border-border p-3 space-y-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium text-foreground">{linkedRecord.full_name}</p>
                            <Badge variant="outline" className="text-[10px]">
                              {selected.client_record_id ? "Vinculada" : "Sugerida"}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">{linkedRecord.email}</p>
                          <p className="text-xs text-muted-foreground">{linkedRecord.phone || "Sem telefone"}</p>
                          {linkedRecord.address && <p className="text-xs text-muted-foreground">{linkedRecord.address}</p>}
                          <div className="flex flex-wrap gap-2 pt-2">
                            <Button size="sm" className="gap-1 text-xs" disabled={linkingRecord} onClick={syncFromRecord}>
                              <RefreshCw size={13} /> {selected.client_record_id ? "Atualizar reserva com a ficha" : "Vincular e atualizar reserva"}
                            </Button>
                            {selected.client_record_id && (
                              <Button size="sm" variant="ghost" className="gap-1 text-xs" disabled={linkingRecord} onClick={unlinkRecord}>
                                <X size={13} /> Desvincular
                              </Button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">Nenhuma ficha vinculada a esta reserva.</p>
                      )}

                      <div className="flex gap-2">
                        <Input
                          value={recordSearch}
                          onChange={(e) => setRecordSearch(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchRecords(); } }}
                          placeholder="Procurar ficha por nome, email ou telefone..."
                          className="text-sm"
                        />
                        <Button size="sm" variant="outline" className="gap-1 shrink-0" disabled={searchingRecords} onClick={searchRecords}>
                          <Search size={14} /> {searchingRecords ? "..." : "Procurar"}
                        </Button>
                      </div>

                      {recordResults.length > 0 && (
                        <div className="space-y-2 max-h-52 overflow-y-auto">
                          {recordResults.map((r) => (
                            <button
                              key={r.id}
                              type="button"
                              disabled={linkingRecord}
                              onClick={() => linkRecordToBooking(r)}
                              className="w-full text-left rounded-lg border border-border p-2.5 hover:border-primary hover:bg-accent/40 transition-colors"
                            >
                              <p className="text-sm font-medium text-foreground">{r.full_name}</p>
                              <p className="text-[11px] text-muted-foreground">{r.email} · {r.phone || "sem telefone"}</p>
                              <p className="text-[10px] text-primary/70 mt-1 italic">Vincular para preencher dados da reserva</p>
                            </button>
                          ))}
                        </div>
                      )}
                      <p className="text-[11px] text-muted-foreground">
                        Ao vincular, o nome, email, telefone (e morada em domicílio) da reserva são atualizados com os dados da ficha.
                      </p>
                    </div>


                    <div className="rounded-xl border border-border p-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CalendarDays size={14} />
                        {format(new Date(selected.booking_date), "d 'de' MMMM 'de' yyyy", { locale: pt })}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock size={14} /> {selected.booking_time.slice(0, 5)}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin size={14} /> {selected.location_type === "home" ? "Domicílio" : "Espaço Mente Sã"}
                      </div>
                      {selected.location_type === "home" && selected.address && (
                        <div className="flex items-start gap-2 text-sm">
                          <Home size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                          <span className="text-muted-foreground flex-1">{selected.address}</span>
                          <Button size="sm" variant="outline" className="shrink-0 gap-1 text-xs" onClick={() => setMapOpen(true)}>
                            <Navigation size={12} /> Ver rota
                          </Button>
                        </div>
                      )}
                    </div>

                    {selected.delivery_fee > 0 && (
                      <div className="rounded-xl border border-border p-4 space-y-1 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Serviço</span><span>€{(Number(selected.total_price) - Number(selected.delivery_fee)).toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Taxa deslocamento</span><span>€{Number(selected.delivery_fee).toFixed(2)}</span></div>
                        <div className="flex justify-between font-bold pt-1 border-t border-border"><span>Total</span><span className="text-primary">€{Number(selected.total_price).toFixed(2)}</span></div>
                      </div>
                    )}

                    {Number(selected.discount_amount || 0) > 0 && (
                      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 space-y-1 text-sm">
                        <div className="flex items-center gap-2 font-semibold text-destructive"><Percent size={14} /> Desconto aplicado</div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Valor original</span><span>€{Number(selected.original_price ?? (Number(selected.total_price) + Number(selected.discount_amount || 0))).toFixed(2)}</span></div>
                        <div className="flex justify-between text-destructive">
                          <span>Desconto {selected.discount_type === "percent" ? `(${Number(selected.discount_value || 0)}%)` : ""}</span>
                          <span>-€{Number(selected.discount_amount).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-bold pt-1 border-t border-border"><span>Total cobrado</span><span className="text-primary">€{Number(selected.total_price).toFixed(2)}</span></div>
                        {selected.discount_by && <p className="text-[11px] text-muted-foreground pt-1">Concedido por: {selected.discount_by}</p>}
                      </div>
                    )}

                    {selected.notes && (
                      <div className="rounded-xl border border-border p-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-1"><FileText size={14} /> Notas</div>
                        <p className="text-sm text-muted-foreground italic">"{selected.notes}"</p>
                      </div>
                    )}

                    {selected.status === "cancelled" && selected.rejection_reason && (
                      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
                        <p className="text-sm font-semibold text-destructive mb-1">Motivo da recusa:</p>
                        <p className="text-sm text-muted-foreground">{selected.rejection_reason}</p>
                      </div>
                    )}

                    {selected.status === "pending" && !rejecting && (
                      <div className="flex gap-3 pt-2">
                        <Button className="flex-1 rounded-full gap-2" onClick={handleConfirm} disabled={processing}>
                          <Check size={16} /> {processing ? "..." : "Confirmar"}
                        </Button>
                        <Button variant="outline" className="flex-1 rounded-full gap-2" onClick={() => setRejecting(true)}>
                          <X size={16} /> Recusar
                        </Button>
                      </div>
                    )}

                    {selected.status === "confirmed" && (
                      <div className="flex gap-3 pt-2">
                        <Button className="flex-1 rounded-full gap-2 bg-blue-600 hover:bg-blue-700" onClick={handleOnTheWay} disabled={processing}>
                          <Car size={16} /> {processing ? "..." : "Estou a Caminho"}
                        </Button>
                      </div>
                    )}

                    {selected.status === "on_the_way" && (
                      <div className="flex gap-3 pt-2">
                        <Button className="flex-1 rounded-full gap-2" onClick={handleComplete} disabled={processing}>
                          <CheckCircle2 size={16} /> {processing ? "..." : "Sessão Concluída"}
                        </Button>
                      </div>
                    )}

                    {selected.status === "pending" && rejecting && (
                      <div className="space-y-3 pt-2">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-foreground">Motivo da recusa *</label>
                          <Textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Indique o motivo..." rows={3} />
                        </div>
                        <div className="flex gap-3">
                          <Button variant="destructive" className="flex-1 rounded-full gap-2" onClick={handleReject} disabled={processing || !rejectionReason.trim()}>
                            <X size={16} /> {processing ? "..." : "Confirmar recusa"}
                          </Button>
                          <Button variant="ghost" className="rounded-full" onClick={() => setRejecting(false)}>Cancelar</Button>
                        </div>
                      </div>
                    )}

                    {/* Alterar status manualmente */}
                    <div className="rounded-xl border border-border p-4 space-y-2">
                      <Label className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <RefreshCw size={14} /> Alterar status manualmente
                      </Label>
                      <Select
                        value={selected.status}
                        onValueChange={(v) => { if (v !== selected.status) handleQuickStatusChange(selected, v); }}
                        disabled={processing}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pendente</SelectItem>
                          <SelectItem value="confirmed">Confirmada</SelectItem>
                          <SelectItem value="on_the_way">A Caminho</SelectItem>
                          <SelectItem value="completed">Concluída</SelectItem>
                          <SelectItem value="cancelled">Recusada</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-muted-foreground">Ao mudar o status, o cliente é notificado automaticamente.</p>
                    </div>

                    {/* Reenviar notificação */}
                    <div className="rounded-xl border border-border p-4 space-y-3">
                      <Label className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Send size={14} /> Reenviar notificação
                      </Label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <Button size="sm" variant="outline" className="gap-1 text-xs" disabled={processing || !selected.client_phone} onClick={() => handleResendNotification("whatsapp")}>
                          <MessageCircle size={13} /> WhatsApp
                        </Button>
                        <Button size="sm" variant="outline" className="gap-1 text-xs" disabled={processing || !selected.client_email} onClick={() => handleResendNotification("email")}>
                          <Mail size={13} /> Email
                        </Button>
                        <Button size="sm" className="gap-1 text-xs" disabled={processing || (!selected.client_email && !selected.client_phone)} onClick={() => handleResendNotification("both")}>
                          <Send size={13} /> Ambos
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Edit Mode */}
            {selected && editing && editForm && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Editar Reserva</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label>Nome</Label><Input value={editForm.client_name} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} /></div>
                    <div><Label>Telefone</Label><Input value={editForm.client_phone} onChange={(e) => setEditForm({ ...editForm, client_phone: e.target.value })} /></div>
                  </div>
                  <div><Label>Email</Label><Input value={editForm.client_email} onChange={(e) => setEditForm({ ...editForm, client_email: e.target.value })} /></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label>Data</Label><Input type="date" value={editForm.booking_date} onChange={(e) => setEditForm({ ...editForm, booking_date: e.target.value })} /></div>
                    <div><Label>Hora</Label><Input type="time" value={editForm.booking_time} onChange={(e) => setEditForm({ ...editForm, booking_time: e.target.value })} /></div>
                  </div>
                  <div>
                    <Label>Serviço</Label>
                    <Select value={editForm.service_id} onValueChange={(v) => setEditForm({ ...editForm, service_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecionar serviço" /></SelectTrigger>
                      <SelectContent>
                        {services.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Local</Label>
                    <Select value={editForm.location_type} onValueChange={(v) => setEditForm({ ...editForm, location_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="home">Domicílio</SelectItem>
                        <SelectItem value="studio">Espaço Mente Sã</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {editForm.location_type === "home" && (
                    <div>
                      <Label>Morada</Label>
                      <AddressAutocomplete
                        value={editForm.address}
                        onChange={(v) => setEditForm({ ...editForm, address: v })}
                        placeholder="Pesquisar endereço em Portugal..."
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label>Preço Base (€)</Label><Input type="number" step="0.01" value={editForm.total_price} onChange={(e) => setEditForm({ ...editForm, total_price: e.target.value })} /></div>
                    <div><Label>Taxa Deslocamento (€)</Label><Input type="number" step="0.01" value={editForm.delivery_fee} onChange={(e) => setEditForm({ ...editForm, delivery_fee: e.target.value })} /></div>
                  </div>

                  {/* Desconto */}
                  <div className="rounded-xl border border-border p-4 space-y-3">
                    <Label className="flex items-center gap-2 text-sm font-semibold"><Percent size={14} className="text-primary" /> Desconto</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <Select value={editForm.discount_type} onValueChange={(v) => setEditForm({ ...editForm, discount_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sem desconto</SelectItem>
                          <SelectItem value="percent">Percentagem (%)</SelectItem>
                          <SelectItem value="fixed">Valor fixo (€)</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number" step="0.01" min="0"
                        disabled={editForm.discount_type === "none"}
                        value={editForm.discount_value}
                        onChange={(e) => setEditForm({ ...editForm, discount_value: e.target.value })}
                        placeholder={editForm.discount_type === "percent" ? "% desconto" : "€ desconto"}
                      />
                    </div>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>€{editBase.toFixed(2)}</span></div>
                      <div className="flex justify-between text-destructive"><span>Desconto</span><span>-€{editDiscountAmount.toFixed(2)}</span></div>
                      <div className="flex justify-between font-bold pt-1 border-t border-border"><span>Total a cobrar</span><span className="text-primary">€{editFinalPrice.toFixed(2)}</span></div>
                    </div>
                    {editDiscountAmount > 0 && (
                      <p className="text-[11px] text-muted-foreground">Desconto registado por: {user?.email || "admin"}</p>
                    )}
                  </div>

                  <div><Label>Notas</Label><Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} /></div>
                  <div className="flex gap-3 pt-2">
                    <Button className="flex-1 rounded-full" onClick={handleSaveEdit} disabled={processing}>
                      {processing ? "A guardar..." : "Guardar Alterações"}
                    </Button>
                    <Button variant="outline" className="rounded-full" onClick={() => setEditing(false)}>Cancelar</Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {selected && selected.address && (
          <RouteMapDialog open={mapOpen} onOpenChange={setMapOpen} clientAddress={selected.address} clientName={selected.client_name} />
        )}

        {/* Manual Booking Dialog */}
        <Dialog open={manualOpen} onOpenChange={setManualOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Nova Reserva Manual</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Client search */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Search size={14} /> Pesquisar cliente existente</Label>
                <Input
                  value={clientSearch}
                  onChange={(e) => searchClients(e.target.value)}
                  placeholder="Pesquisar por nome ou telefone..."
                />
                {clientResults.length > 0 && (
                  <div className="border border-border rounded-lg overflow-hidden">
                    {clientResults.map((c) => (
                      <button
                        key={c.user_id}
                        onClick={() => selectClient(c)}
                        className="w-full text-left px-4 py-2.5 hover:bg-accent/50 transition-colors border-b border-border last:border-0 text-sm"
                      >
                        <span className="font-medium text-foreground">{c.full_name}</span>
                        {c.phone && <span className="text-muted-foreground ml-2">· {c.phone}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {searchingClients && <p className="text-xs text-muted-foreground">A pesquisar...</p>}
              </div>

              <div className="border-t border-border pt-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Nome *</Label><Input value={manualForm.client_name} onChange={(e) => setManualForm({ ...manualForm, client_name: e.target.value })} /></div>
                  <div><Label>Telefone</Label><Input value={manualForm.client_phone} onChange={(e) => setManualForm({ ...manualForm, client_phone: e.target.value })} /></div>
                </div>
                <div><Label>Email</Label><Input value={manualForm.client_email} onChange={(e) => setManualForm({ ...manualForm, client_email: e.target.value })} /></div>

                <div>
                  <Label>Serviço</Label>
                  <Select value={manualForm.service_id} onValueChange={(v) => {
                    const svc = services.find(s => s.id === v);
                    setManualForm({ ...manualForm, service_id: v, total_price: svc?.price || 0 });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Selecionar serviço" /></SelectTrigger>
                    <SelectContent>
                      {services.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}, €{s.price}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Data *</Label><Input type="date" value={manualForm.booking_date} onChange={(e) => setManualForm({ ...manualForm, booking_date: e.target.value })} /></div>
                  <div>
                    <Label>Hora *</Label>
                    <Select value={manualForm.booking_time} onValueChange={(v) => setManualForm({ ...manualForm, booking_time: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                      <SelectContent>
                        {allTimeSlots.map((slot) => (<SelectItem key={slot} value={slot}>{slot}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Local</Label>
                  <Select value={manualForm.location_type} onValueChange={(v) => setManualForm({ ...manualForm, location_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="studio">Espaço Mente Sã</SelectItem>
                      <SelectItem value="home">Domicílio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {manualForm.location_type === "home" && (
                  <div>
                    <Label>Morada</Label>
                    <AddressAutocomplete
                      value={manualForm.address}
                      onChange={(v) => setManualForm({ ...manualForm, address: v })}
                      placeholder="Pesquisar endereço..."
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Preço Total (€)</Label><Input type="number" step="0.01" value={manualForm.total_price} onChange={(e) => setManualForm({ ...manualForm, total_price: Number(e.target.value) })} /></div>
                  <div><Label>Taxa Desloc. (€)</Label><Input type="number" step="0.01" value={manualForm.delivery_fee} onChange={(e) => setManualForm({ ...manualForm, delivery_fee: Number(e.target.value) })} /></div>
                </div>

                {staffList.length > 0 && (
                  <div>
                    <Label>Colaborador</Label>
                    <Select value={manualForm.staff_id} onValueChange={(v) => setManualForm({ ...manualForm, staff_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecionar (opcional)" /></SelectTrigger>
                      <SelectContent>
                        {staffList.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div><Label>Notas</Label><Textarea value={manualForm.notes} onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })} rows={2} /></div>

                <Button className="w-full rounded-full" onClick={handleCreateManualBooking} disabled={processing}>
                  {processing ? "A criar..." : "Criar Reserva"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Block Slots Dialog */}
        <Dialog open={blockOpen} onOpenChange={(open) => {
          setBlockOpen(open);
          if (!open) {
            setBlockDates([]);
            setBlockTimes([]);
            setBlockStaffId("");
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Bloquear Horários</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Colaborador *</Label>
                <Select value={blockStaffId} onValueChange={(v) => { setBlockStaffId(v); setBlockTimes([]); }}>
                  <SelectTrigger><SelectValue placeholder="Selecionar colaborador" /></SelectTrigger>
                  <SelectContent>
                    {staffList.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Selecione as Datas</Label>
                <div className="border border-border rounded-xl p-2 bg-muted/20">
                  <UICalendar
                    mode="multiple"
                    selected={blockDates}
                    onSelect={(dates) => {
                      setBlockDates(dates || []);
                      setBlockTimes([]);
                    }}
                    locale={pt}
                    className="rounded-md border-none"
                  />
                </div>
                {blockDates.length > 0 && (
                  <p className="text-[10px] text-muted-foreground">
                    {blockDates.length} dia(s) selecionado(s)
                  </p>
                )}
              </div>

              {blockDates.length > 0 && blockStaffId && (
                <div className="space-y-2">
                  <Label>Selecione os horários a bloquear</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {getTimeSlotsForDateStr(format(blockDates[0], "yyyy-MM-dd"), blockStaffId).map((slot) => {
                      const isBooked = bookedSlotsForBlock.includes(slot);
                      const isSelected = blockTimes.includes(slot);
                      return (
                        <button
                          key={slot}
                          disabled={isBooked}
                          onClick={() => toggleBlockTime(slot)}
                          className={`rounded-lg py-2 text-sm font-medium transition-all ${
                            isBooked
                              ? "bg-muted text-muted-foreground/40 cursor-not-allowed line-through"
                              : isSelected
                              ? "bg-destructive text-destructive-foreground"
                              : "border border-border hover:border-primary/50"
                          }`}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <Button
                className="w-full rounded-full gap-2"
                disabled={blockDates.length === 0 || !blockStaffId || blockTimes.length === 0 || processing}
                onClick={handleBlockSlots}
              >
                <Lock size={14} /> {processing ? "A bloquear..." : `Bloquear ${blockTimes.length} horário(s)`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Envio da Ficha de Avaliação após confirmação */}
        <Dialog open={!!recordPrompt} onOpenChange={(o) => !o && setRecordPrompt(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText size={18} className="text-primary" /> Ficha de Avaliação e Consentimento
              </DialogTitle>
            </DialogHeader>
            {recordPrompt && (
              <div className="space-y-4">
                <div className="rounded-xl border border-border p-3 text-sm">
                  <p className="font-semibold">{recordPrompt.booking.client_name}</p>
                  <p className="text-muted-foreground text-xs">{recordPrompt.booking.client_email}</p>
                  <p className="text-muted-foreground text-xs">{recordPrompt.booking.client_phone}</p>
                </div>

                {recordPrompt.hasRecord ? (
                  <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <p className="font-semibold mb-1">Este cliente já tem ficha preenchida.</p>
                    <p className="text-xs">
                      Não será enviado o formulário. Se enviar, o cliente recebe apenas um aviso de que a ficha já
                      está registada e que, para preencher novamente, deve solicitar por email ou WhatsApp.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Deseja enviar o link da ficha de avaliação e consentimento ao cliente?
                  </p>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    className="rounded-full gap-2"
                    disabled={sendingRecord || !recordPrompt.booking.client_email}
                    onClick={() => sendClientRecord(["email"])}
                  >
                    <Mail size={14} /> Email
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-full gap-2"
                    disabled={sendingRecord || !recordPrompt.booking.client_phone}
                    onClick={() => sendClientRecord(["whatsapp"])}
                  >
                    <MessageCircle size={14} /> WhatsApp
                  </Button>
                </div>
                <Button
                  className="w-full rounded-full gap-2"
                  disabled={sendingRecord}
                  onClick={() => sendClientRecord(["email", "whatsapp"])}
                >
                  <Send size={14} /> {sendingRecord ? "A enviar..." : "Enviar por ambos"}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full rounded-full"
                  disabled={sendingRecord}
                  onClick={() => setRecordPrompt(null)}
                >
                  Não enviar
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Bulk Edit Dialog */}
      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Alterar Colaborador em Massa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Alterar o colaborador para os {selectedBookings.size} itens selecionados.
            </p>
            <div>
              <Label>Novo Colaborador</Label>
              <Select value={bulkStaffId} onValueChange={setBulkStaffId}>
                <SelectTrigger><SelectValue placeholder="Selecionar colaborador" /></SelectTrigger>
                <SelectContent>
                  {staffList.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              className="w-full rounded-full" 
              disabled={!bulkStaffId || processing} 
              onClick={handleBulkUpdateStaff}
            >
              {processing ? "A atualizar..." : "Atualizar Todos"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
};

export default Agenda;
